/**
 * "CDD status meanings" tour.
 *
 * Pure modal-style explainer. The platform has TWO related-but-distinct
 * status fields on each client:
 *
 *   1. cdd_status      — where the client is in the CDD workflow itself
 *   2. computed_status — overall compliance state, derived from CDD + escalations
 *
 * This tour clarifies both, since they appear in different places (CDD column
 * dot vs row-level Status pill) and users frequently confuse them.
 *
 * Dot/pill colours mirror ClientList.vue scoped CSS (`.cdd_*` rules around
 * line 458-498) and AmlClient.php's computed_status logic — keep in sync.
 */

import { NEXT, BACK, START, SKIP, DONE } from './_helpers.js';

// CDD status dot — small filled circle, matches ClientList.vue
const dot = (bg) =>
    `<span style="display:inline-block;width:10px;height:10px;border-radius:50%;background:${bg};vertical-align:middle;margin-right:6px;"></span>`;

const dotNotStarted = dot('#d1d5db');
const dotInProgress = dot('#2563eb');
const dotComplete   = dot('#16a34a');
const dotOverdue    = dot('#dc2626');

// Computed-status pill (also matches the row-status badge in ClientList).
const PILL_BASE = 'display:inline-block;font-size:11px;font-weight:700;padding:2px 10px;border-radius:8px;letter-spacing:0.3px;';
const pill = (label, bg, fg) =>
    `<span style="${PILL_BASE}background:${bg};color:${fg};">${label}</span>`;

const pillClear      = pill('Clear',      '#dcfce7', '#166534');
const pillPending    = pill('Pending',    '#fef3c7', '#92400e');
const pillRestricted = pill('Restricted', '#fee2e2', '#991b1b');
const pillOffboarded = pill('Offboarded', '#e5e7eb', '#374151');

export const cddStatusTour = {
    id: 'cdd-status',
    title: 'CDD & client status meanings',
    description: 'What each CDD status (Not Started / In Progress / Complete / Overdue) means, and how it relates to a client\'s overall compliance status (Clear / Pending / Restricted / Offboarded).',
    autoLaunchOn: { page: 'clients' },

    steps: [
        {
            id: 'intro',
            title: 'Two status fields, not one',
            text: `Every client has two status indicators that look similar but mean
                different things:
                <ul style="margin:6px 0 0 18px;padding:0;">
                    <li>The <strong>CDD Status</strong> — a coloured dot in the CDD
                        column. Tracks where the client is in the customer-due-diligence
                        process itself.</li>
                    <li>The <strong>Client Status</strong> — a pill in the Status column.
                        Overall compliance state, computed from CDD + any open escalations.</li>
                </ul>
                Let's walk through both.`,
            buttons: [START],
        },
        {
            id: 'cdd-not-started',
            title: 'CDD: Not Started',
            text: `<div style="margin-bottom:8px;">${dotNotStarted} <strong>Not Started</strong></div>
                The client record exists but no CDD work has begun. Typical for a
                client who has just been added but the verification step hasn\u2019t
                started.`,
            buttons: [BACK, NEXT],
        },
        {
            id: 'cdd-in-progress',
            title: 'CDD: In Progress',
            text: `<div style="margin-bottom:8px;">${dotInProgress} <strong>In Progress</strong></div>
                CDD work is under way — risk assessment done, identity verification
                started, but the file isn\u2019t complete yet (e.g. waiting on
                ID verification, beneficial-owner info, or SM approval for high-risk).`,
            buttons: [BACK, NEXT],
        },
        {
            id: 'cdd-complete',
            title: 'CDD: Complete',
            text: `<div style="margin-bottom:8px;">${dotComplete} <strong>Complete</strong></div>
                All required CDD steps for this client\u2019s risk rating have been
                done and signed off. The client can be provided with designated services.`,
            buttons: [BACK, NEXT],
        },
        {
            id: 'cdd-overdue',
            title: 'CDD: Overdue',
            text: `<div style="margin-bottom:8px;">${dotOverdue} <strong>Overdue</strong></div>
                A periodic CDD review was due and has passed. Action needed: re-run the
                risk assessment, re-verify any expired ID, and update the client record.
                Overdue CDD is one of the things that surfaces under <em>Reviews overdue</em>
                on the dashboard.`,
            buttons: [BACK, NEXT],
        },
        {
            id: 'cdd-set',
            title: 'How CDD status gets set',
            text: `CDD status is <strong>computed automatically</strong> — you don’t set it directly. The system derives it from:
                <ul style="margin:6px 0 0 18px;padding:0;">
                    <li>Whether identity verification has been run and what its outcome was.</li>
                    <li>Whether each associated person’s identity is verified.</li>
                    <li>For entity clients, whether the entity (KYB) check has been confirmed.</li>
                </ul>
                As you complete those tasks in the <em>Customer Due Diligence</em> section of the client’s detail page, the dot moves on its own from Not Started → In Progress → Complete. <strong>Overdue</strong> is reached when the client’s next review date passes without a periodic review being recorded.`,
            buttons: [BACK, NEXT],
        },
        {
            id: 'computed-intro',
            title: 'Now the Client Status pill',
            text: `The pill in the Status column is a <strong>computed</strong> overall
                state — you don\u2019t set it directly. The system derives it from CDD
                progress, open escalations, and offboarding state.
                <br><br>
                Four possible values:`,
            buttons: [BACK, NEXT],
        },
        {
            id: 'computed-clear',
            title: 'Clear',
            text: `<div style="margin-bottom:8px;">${pillClear}</div>
                CDD is complete, no open escalations, no compliance issues. Safe to
                provide designated services. Shown with a shield-check icon.`,
            buttons: [BACK, NEXT],
        },
        {
            id: 'computed-pending',
            title: 'Pending',
            text: `<div style="margin-bottom:8px;">${pillPending}</div>
                CDD isn\u2019t finished yet — Not Started, In Progress, or Overdue.
                The row is also tinted in the client list to nudge you to finish the
                work. Shown with a clock icon.`,
            buttons: [BACK, NEXT],
        },
        {
            id: 'computed-restricted',
            title: 'Restricted',
            text: `<div style="margin-bottom:8px;">${pillRestricted}</div>
                There\u2019s an open compliance issue — typically an unresolved
                escalation, a sanctions hold, or a CO instruction to suspend services.
                Designated services should not be provided until this is cleared.
                Shown with a shield-exclamation icon.`,
            buttons: [BACK, NEXT],
        },
        {
            id: 'computed-offboarded',
            title: 'Offboarded',
            text: `<div style="margin-bottom:8px;">${pillOffboarded}</div>
                The relationship with this client has been formally closed. The record
                stays on file (7-year retention) but no further service is being
                provided. Shown with a slash-circle icon.`,
            buttons: [BACK, DONE],
        },
    ],
};
